import { useState, useEffect } from 'react';
import { useSettings } from '@/context/SettingsContext';
import { Settings as SettingsIcon, Save, Calendar, DollarSign, Moon, Sun, Bell, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { connectUSBPrinter, disconnectUSBPrinter } from '@/utils/thermalPrinter';

const Settings = () => {
  const { settings, updateSettings } = useSettings();
  const [formData, setFormData] = useState(settings);

  // Sync with settings from context (e.g. after backend fetch)
  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleSave = async () => {
    await updateSettings(formData);
    toast.success('Settings saved successfully');
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: SettingsIcon },
  ];

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Settings
          </h1>
          <p className="text-zinc-500">Configure your app</p>
      </div>

      <div className="max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Date Format */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Date Format
                </h2>
                <p className="text-sm text-zinc-500">Choose how dates are displayed</p>
              </div>
            </div>
            <div className="space-y-3">
              {['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'].map((format) => (
                <label
                  key={format}
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.dateFormat === format
                      ? 'border-lime-400 bg-lime-50'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="dateFormat"
                    value={format}
                    checked={formData.dateFormat === format}
                    onChange={(e) => setFormData({ ...formData, dateFormat: e.target.value })}
                    className="w-4 h-4 text-lime-600"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-zinc-900">{format}</div>
                    <div className="text-xs text-zinc-500">
                      Example: {new Date().toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: format.startsWith('MM') ? '2-digit' : format.startsWith('DD') ? '2-digit' : 'numeric',
                        day: '2-digit'
                      })}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Currency Symbol */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="text-green-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Currency
                </h2>
                <p className="text-sm text-zinc-500">Select your currency symbol</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {['$', '€', '£', '¥', '₹', '₽', 'kr', 'R$'].map((symbol) => (
                <button
                  key={symbol}
                  onClick={() => setFormData({ ...formData, currency: symbol })}
                  className={`p-4 border-2 rounded-lg font-bold text-xl transition-all ${
                    formData.currency === symbol
                      ? 'border-lime-400 bg-lime-50 text-lime-700'
                      : 'border-zinc-200 hover:border-zinc-300 text-zinc-600'
                  }`}
                >
                  {symbol}
                </button>
              ))}
            </div>
            <div className="mt-4 p-3 bg-zinc-50 rounded-lg">
              <div className="text-sm text-zinc-600">Preview:</div>
              <div className="text-2xl font-bold text-zinc-900 mt-1">
                {formData.currency}1,234.56
              </div>
            </div>
          </div>

          {/* Theme Selection */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Moon className="text-purple-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Theme
                </h2>
                <p className="text-sm text-zinc-500">Choose your preferred theme</p>
              </div>
            </div>
            <div className="space-y-3">
              {themeOptions.map(({ value, label, icon: Icon }) => (
                <label
                  key={value}
                  className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.theme === value
                      ? 'border-lime-400 bg-lime-50'
                      : 'border-zinc-200 hover:border-zinc-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="theme"
                    value={value}
                    checked={formData.theme === value}
                    onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                    className="w-4 h-4 text-lime-600"
                  />
                  <Icon size={20} className="text-zinc-600" />
                  <div className="flex-1 font-medium text-zinc-900">{label}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Membership Reminder */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Bell className="text-orange-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Membership Reminders
                </h2>
                <p className="text-sm text-zinc-500">Notify before membership expires</p>
              </div>
            </div>
            <div>
              <Label htmlFor="membershipDueReminder">Days Before Expiry</Label>
              <Input
                id="membershipDueReminder"
                type="number"
                min="1"
                max="30"
                value={formData.membershipDueReminder}
                onChange={(e) => setFormData({ ...formData, membershipDueReminder: parseInt(e.target.value) })}
                className="mt-2"
              />
              <p className="text-xs text-zinc-500 mt-2">
                Members will be flagged {formData.membershipDueReminder} days before their membership expires
              </p>
            </div>
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="text-sm font-medium text-orange-700">Example Alert:</div>
              <div className="text-xs text-orange-600 mt-1">
                "John's membership expires in {formData.membershipDueReminder} days"
              </div>
            </div>
          </div>

          {/* Thermal Printer */}
          <div className="bg-white border border-zinc-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Printer className="text-teal-600" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Thermal Printer
                </h2>
                <p className="text-sm text-zinc-500">Configure direct thermal printing</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Connection Type</Label>
                <div className="mt-2 flex gap-2">
                  {['none','usb','bluetooth'].map((m) => (
                    <button
                      key={m}
                      onClick={() => setFormData({ ...formData, thermalPrinter: { ...formData.thermalPrinter, mode: m } })}
                      className={`px-4 py-2 border-2 rounded-lg text-sm ${formData.thermalPrinter?.mode === m ? 'border-lime-400 bg-lime-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                    >
                      {m.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              {formData.thermalPrinter?.mode === 'usb' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="usbVendorId">USB Vendor ID</Label>
                    <Input
                      id="usbVendorId"
                      value={formData.thermalPrinter.usbVendorId}
                      onChange={(e) => setFormData({ ...formData, thermalPrinter: { ...formData.thermalPrinter, usbVendorId: e.target.value } })}
                      placeholder="e.g. 0x04B8"
                    />
                  </div>
                  <div>
                    <Label htmlFor="usbProductId">USB Product ID</Label>
                    <Input
                      id="usbProductId"
                      value={formData.thermalPrinter.usbProductId}
                      onChange={(e) => setFormData({ ...formData, thermalPrinter: { ...formData.thermalPrinter, usbProductId: e.target.value } })}
                      placeholder="e.g. 0x0E15"
                    />
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          const v = parseInt(formData.thermalPrinter.usbVendorId);
                          const p = parseInt(formData.thermalPrinter.usbProductId);
                          const ok = await connectUSBPrinter(v, p);
                          if (ok) {
                            const next = { ...formData, thermalPrinter: { ...formData.thermalPrinter, paired: true } };
                            setFormData(next);
                            updateSettings(next);
                            toast.success('USB printer connected');
                          } else {
                            toast.error('Failed to connect USB printer');
                          }
                        } catch {
                          toast.error('Invalid Vendor/Product ID');
                        }
                      }}
                      className="bg-teal-500 hover:bg-teal-600 text-white"
                    >
                      Connect USB Printer
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        disconnectUSBPrinter();
                        const next = { ...formData, thermalPrinter: { ...formData.thermalPrinter, paired: false } };
                        setFormData(next);
                        updateSettings(next);
                        toast.success('USB printer disconnected');
                      }}
                    >
                      Disconnect
                    </Button>
                  </div>
                  <div className="col-span-2 text-xs text-zinc-500">
                    Use Chrome on HTTPS for WebUSB.
                  </div>
                </div>
              )}
              {formData.thermalPrinter?.mode === 'bluetooth' && (
                <div className="text-sm text-zinc-600">
                  Printing uses the system dialog due to browser Bluetooth limitations.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Save Button */}
      <div className="mt-8 flex justify-end">
        <Button
          onClick={handleSave}
          className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold px-8 py-6 text-lg"
        >
          <Save size={20} className="mr-2" />
          Save Settings
        </Button>
      </div>
      </div>
    </div>
  );
};

export default Settings;
